import { orderBy } from 'lodash';

const STREAM_INF_TAG = '#EXT-X-STREAM-INF:';

type Variant = {
  tagLineIndex: number;
  uriLineIndex: number;
  pixels: number;
  bandwidth: number;
};

function isUriLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.length > 0 && !trimmed.startsWith('#');
}

function findVariants(lines: string[]): Variant[] {
  const variants: Variant[] = [];
  lines.forEach((line, tagLineIndex) => {
    if (!line.startsWith(STREAM_INF_TAG)) return;

    const uriLineIndex = lines.findIndex(
      (other, index) => index > tagLineIndex && isUriLine(other),
    );
    if (uriLineIndex === -1) return;

    const resolution = /RESOLUTION=(\d+)x(\d+)/.exec(line);
    const bandwidth = /[:,]BANDWIDTH=(\d+)/.exec(line);
    variants.push({
      tagLineIndex,
      uriLineIndex,
      pixels: resolution ? Number(resolution[1]) * Number(resolution[2]) : 0,
      bandwidth: bandwidth ? Number(bandwidth[1]) : 0,
    });
  });
  return variants;
}

/**
 * Reduces a multivariant playlist to its highest resolution (then highest bandwidth) variant, so
 * the player can never fall back to a lower quality. Media playlists come back unchanged.
 */
export function pinHighestVariant(playlist: string): string {
  const lines = playlist.split(/\r?\n/);
  const variants = findVariants(lines);
  if (variants.length < 2) return playlist;

  const [highest] = orderBy(variants, ['pixels', 'bandwidth'], ['desc', 'desc']);
  const droppedLineIndexes = new Set(
    variants
      .filter((variant) => variant !== highest)
      .flatMap((variant) => [variant.tagLineIndex, variant.uriLineIndex]),
  );
  return lines.filter((_, index) => !droppedLineIndexes.has(index)).join('\n');
}

/** Absolute URLs of the variant playlists listed in a multivariant playlist. */
export function findVariantUrls(playlist: string, playlistUrl: string): string[] {
  const lines = playlist.split(/\r?\n/);
  return findVariants(lines).map(
    (variant) => new URL(lines[variant.uriLineIndex].trim(), playlistUrl).href,
  );
}

/** Absolute URLs of the segments listed in a media playlist. */
export function findSegmentUrls(playlist: string, playlistUrl: string): string[] {
  return playlist
    .split(/\r?\n/)
    .filter(isUriLine)
    .map((line) => new URL(line.trim(), playlistUrl).href);
}
